import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { savedSearchAPI } from '@/services/api';
import { Bookmark, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

/**
 * Save Search button — only renders when:
 *  - user is logged in
 *  - the current page has at least one query param (so there's something to save)
 *
 * Props:
 *  - adType: "horse" | "product" | "all" (required)
 *  - label: short human-readable summary (optional, e.g. "Lusitanos near Lisbon")
 *  - basePath: override the path stored. Defaults to current location.pathname + search.
 */
export default function SaveSearchButton({ adType, label, basePath, isMobile = false }) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [name, setName] = useState('');
	const [frequency, setFrequency] = useState('off');
	const [saving, setSaving] = useState(false);

	if (!user) return null;
	if (!location.search) return null; // nothing to save on an empty page

	const handleSave = async () => {
		if (!name.trim()) {
			toast.error(t('savedSearch.name_required'));
			return;
		}
		setSaving(true);
		try {
			const path = basePath || (location.pathname + location.search);
			await savedSearchAPI.create({
				name: name.trim(),
				ad_type: adType,
				path,
				label: label || null,
				alert_frequency: frequency,
			});
			toast.success(t('savedSearch.saved'));
			setOpen(false);
			setName('');
			setFrequency('off');
		} catch (e) {
			const msg = e?.response?.data?.detail || t('savedSearch.error');
			toast.error(msg);
		}
		setSaving(false);
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className={`${ isMobile ? 'flex md:hidden w-full h-10 justify-center' : 'hidden md:flex gap-2'} items-center px-4 py-2 bg-white border 
						border-[#E6E2D6] rounded-sm text-sm text-[#1A3C34] hover:border-[#8B4513] transition-colors`}
				data-testid="save-search-btn">
				<Bookmark className="w-4 h-4 text-[#8B4513]" />
				{t('savedSearch.save')}
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-md" data-testid="save-search-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display']">
							{t('savedSearch.dialog_title')}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 pt-2">
						<p className="text-xs text-[#666055]">
							{t('savedSearch.dialog_help')}
						</p>
						<Input
							autoFocus
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t('savedSearch.name_placeholder')}
							maxLength={80}
							onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
							data-testid="save-search-name-input"
						/>
						<div>
							<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
								{t('savedSearch.alert_frequency')}
							</label>
							<Select value={frequency} onValueChange={setFrequency}>
								<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
															 data-testid="save-search-frequency-select">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="off">{t('savedSearch.freq_off')}</SelectItem>
									<SelectItem value="daily">{t('savedSearch.freq_daily')}</SelectItem>
									<SelectItem value="weekly">{t('savedSearch.freq_weekly')}</SelectItem>
								</SelectContent>
							</Select>
							<p className="text-[10px] text-[#666055]/80 mt-1">{t('savedSearch.alert_help')}</p>
						</div>
						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="ghost"
								onClick={() => setOpen(false)}
								className="text-[#666055]"
								data-testid="save-search-cancel"
							>
								<X className="w-4 h-4 mr-1" /> {t('common.cancel')}
							</Button>
							<Button
								onClick={handleSave}
								disabled={saving || !name.trim()}
								className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm btn-equi"
								data-testid="save-search-confirm"
							>
								<Bookmark className="w-4 h-4 mr-1" />
								{saving ? t('common.loading') : t('savedSearch.save')}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}